import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollText, Loader2, Wand2, ClipboardPaste, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import { importApi, modelsApi, documentsApi } from '@/lib/api';
import { usePW } from '@/lib/store';
// iter-53m — Post-import Claude offer modal.
import SynopsisFollowupDialog from './SynopsisFollowupDialog';
// iter-53n — Chapter Bible "fill the gaps" popup (runs before the followup).
import ChapterBibleDialog from './ChapterBibleDialog';

const GENRES = ['fantasy', 'sci-fi', 'thriller', 'romance', 'mystery', 'literary', 'horror', 'historical'];

export function ImportSynopsisDialog({ open, onOpenChange, initialFile = null }) {
  const { loadProject, refreshProjects } = usePW();
  const [models, setModels] = useState([]);
  const [name, setName] = useState('');
  const [author, setAuthor] = useState('David Willis');
  const [genre, setGenre] = useState('romance');
  const [chaptersCount, setChaptersCount] = useState(8);
  const [wordsPerChapter, setWordsPerChapter] = useState(2500);
  const [modelId, setModelId] = useState('claude-sonnet-4-5');
  const [synopsis, setSynopsis] = useState('');
  const [guidance, setGuidance] = useState('');
  const [autoDraft, setAutoDraft] = useState(false);
  // iter55f86 — When off, scaffolded chapters are labelled "Chapter N"
  // instead of the LLM's outline titles. Users can rename any chapter
  // afterwards from the sidebar.
  const [chapterTitlesEnabled, setChapterTitlesEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  // iter-53m — When the import succeeds WITHOUT auto-draft, capture the
  // new project + synopsis text and pop the follow-up offer modal.
  const [followup, setFollowup] = useState(null);
  // iter-53n — Chapter Bible "fill the gaps" popup runs BEFORE the
  // followup-offer modal so the user enriches missing character details
  // before drafting begins.
  const [bible, setBible] = useState(null);
  // iter55z-e — Paste/upload doc helpers.
  const fileInputRef = useRef(null);

  /* ── Shared file-processing helper ─────────────────────────────── */
  const processFile = async (f) => {
    if (!f) return;
    if (f.size > 6_000_000) { toast.error('File too large (max 6 MB).'); return; }
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    const isPlain = ['txt', 'md', 'markdown'].includes(ext);
    const isRich  = ['docx', 'pdf', 'rtf', 'doc'].includes(ext);
    if (!isPlain && !isRich) {
      toast.warning('Unsupported type. Use .txt, .md, .rtf, .docx, or .pdf.');
      return;
    }
    setBusy(true);
    try {
      let text = '';
      if (isPlain) {
        text = await f.text();
      } else {
        const res = await documentsApi.extractText(f);
        text = res?.text || '';
      }
      if (!text.trim()) {
        toast.warning('No readable text found in that file.');
      } else {
        setSynopsis((prev) => (prev.trim() ? `${prev.trim()}\n\n${text}` : text));
        toast.success(`Loaded ${f.name} (${text.length.toLocaleString()} chars).`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to read file.');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /* ── Auto-process initialFile when dialog opens ─────────────────── */
  useEffect(() => {
    if (open && initialFile) processFile(initialFile);
  }, [open, initialFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFilePicked = async (evt) => {
    const f = evt.target.files?.[0];
    processFile(f);
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        toast.warning('Clipboard is empty.');
        return;
      }
      // Append rather than overwrite if the box already has content.
      setSynopsis((prev) => (prev.trim() ? `${prev.trim()}\n\n${text}` : text));
      toast.success(`Pasted ${text.length.toLocaleString()} characters.`);
    } catch {
      toast.error('Clipboard access blocked. Paste with Cmd/Ctrl+V into the box.');
    }
  };

  useEffect(() => {
    if (!open) return;
    modelsApi.list().then((r) => setModels(r.models || [])).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!name.trim()) { toast.warning('Give the project a name.'); return; }
    if (synopsis.trim().length < 80) {
      toast.warning('Synopsis must be at least 80 characters of plot summary.');
      return;
    }
    setBusy(true);
    try {
      const r = await importApi.synopsis({
        name, author, genre,
        chapters_count: chaptersCount,
        words_per_chapter: wordsPerChapter,
        model_id: modelId,
        synopsis: synopsis.trim(),
        user_guidance: guidance.trim(),
        auto_draft: autoDraft,
        chapter_titles_enabled: chapterTitlesEnabled,
      });
      const totalWords = r.build_summary?.total_words;
      const buildStatus = r.build_summary?.status;
      if (autoDraft && buildStatus === 'in_progress') {
        toast.success(
          `Project scaffolded. Auto-draft running in the background — chapters will appear as they complete (~15s each).`,
        );
      } else if (autoDraft && totalWords) {
        toast.success(`Project created & drafted (${totalWords.toLocaleString()} words). Switched to Draft mode.`);
      } else {
        toast.success(`Project scaffolded with ${r.outline_lines_returned}/${chaptersCount} chapter outlines.`);
      }
      await refreshProjects();
      await loadProject(r.project.id, { silent: true });
      onOpenChange(false);
      // Reset for next open
      setName(''); setGuidance(''); setAutoDraft(false);
      // iter-53m — If the user did NOT pre-elect auto-draft, surface
      // the Chapter Bible "fill the gaps" popup first, and queue the
      // Claude follow-up offer to open right after the user dismisses
      // it. Two sequential modals keep each step focused.
      if (!autoDraft) {
        setBible({ project: r.project, synopsis: synopsis.trim() });
      }
      setSynopsis('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Synopsis import failed.');
    } finally { setBusy(false); }
  };

  const synopsisOk = synopsis.trim().length >= 80;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="import-synopsis-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <ScrollText className="h-4 w-4 text-primary" /> Create from synopsis
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Paste a plot summary and The Loom&apos;s AI will scaffold a chapter-by-chapter outline. Enable Auto-draft to generate the full first draft now and switch the project to Draft mode on completion.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="md:col-span-2 space-y-3">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[11px]">Synopsis (plot summary)</Label>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={pasteFromClipboard}
                    className="h-6 gap-1 px-2 text-[10.5px]"
                    data-testid="synopsis-paste-btn"
                  >
                    <ClipboardPaste className="h-3 w-3" />
                    Paste
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={busy}
                    className="h-6 gap-1 px-2 text-[10.5px]"
                    data-testid="synopsis-upload-btn"
                  >
                    <FileUp className="h-3 w-3" />
                    Upload doc
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.md,.markdown,.rtf,.docx,.doc,.pdf,text/plain,text/markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={onFilePicked}
                    className="hidden"
                    data-testid="synopsis-file-input"
                  />
                </div>
              </div>
              <Textarea
                value={synopsis}
                onChange={(e) => setSynopsis(e.target.value)}
                placeholder="A young magister discovers a hidden map that leads to a forgotten city beneath the ivy gardens of her university..."
                rows={9}
                data-testid="synopsis-text"
                className="mt-1 resize-none font-serif-novel text-[13px] leading-relaxed"
              />
              <div className="mt-1 flex items-center justify-between text-[10.5px]">
                <span className={synopsisOk ? 'text-success' : 'text-muted-foreground'}>
                  {synopsis.trim().length} / 80 chars min
                </span>
                <span className="text-muted-foreground">~{Math.ceil(synopsis.trim().split(/\s+/).filter(Boolean).length / 1)} words</span>
              </div>
            </div>
            <div>
              <Label className="text-[11px]">Author guidance (optional)</Label>
              <Textarea
                value={guidance}
                onChange={(e) => setGuidance(e.target.value)}
                placeholder="Tone: lyrical, slightly archaic. POV: 3rd limited on Magus. Avoid contemporary slang."
                rows={3}
                data-testid="synopsis-guidance"
                className="mt-1 resize-none text-[12px]"
              />
            </div>
          </div>

          <div className="space-y-2.5">
            <div>
              <Label className="text-[11px]">Project name</Label>
              <Input className="mt-1 h-8 text-[12px]" value={name} onChange={(e) => setName(e.target.value)} data-testid="synopsis-name" />
            </div>
            <div>
              <Label className="text-[11px]">Author</Label>
              <Input className="mt-1 h-8 text-[12px]" value={author} onChange={(e) => setAuthor(e.target.value)} />
            </div>
            <div>
              <Label className="text-[11px]">Genre</Label>
              <select
                className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12px] capitalize"
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                data-testid="synopsis-genre"
              >
                {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px]">Chapters</Label>
                <Input
                  className="mt-1 h-8 font-mono-stat text-[12px]"
                  type="number" min={5} max={25}
                  value={chaptersCount}
                  onChange={(e) => setChaptersCount(parseInt(e.target.value, 10) || 8)}
                  data-testid="synopsis-chapters"
                />
              </div>
              <div>
                <Label className="text-[11px]">Words / ch</Label>
                <Input
                  className="mt-1 h-8 font-mono-stat text-[12px]"
                  type="number" min={1000} max={6000}
                  value={wordsPerChapter}
                  onChange={(e) => setWordsPerChapter(parseInt(e.target.value, 10) || 2500)}
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px]">AI model</Label>
              <select
                className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                data-testid="synopsis-model"
              >
                {models.length === 0
                  ? <option value="claude-sonnet-4-5">Claude Sonnet 4.5</option>
                  : models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>

            <label
              className="mt-2 flex cursor-pointer items-start gap-2 rounded-md border border-warning/30 bg-warning/5 p-2 text-[11px]"
              data-testid="synopsis-autodraft-row"
            >
              <Checkbox
                checked={autoDraft}
                onCheckedChange={(v) => setAutoDraft(!!v)}
                data-testid="synopsis-autodraft"
              />
              <div>
                <div className="font-medium text-foreground">Auto-draft entire novel now</div>
                <div className="text-muted-foreground">
                  Drafts every chapter with AI in one pass and switches the project to <b>Draft</b> mode when done. Higher token cost (~${(chaptersCount * wordsPerChapter * 0.0000045).toFixed(2)} estimated).
                </div>
              </div>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={busy || !synopsisOk || !name.trim()}
            data-testid="synopsis-submit"
            className="gap-1.5"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
            {busy ? (autoDraft ? 'Drafting novel…' : 'Scaffolding…')
                  : (autoDraft ? 'Scaffold & draft' : 'Scaffold outline')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {/* iter-53n — Chapter Bible "fill the gaps" popup. Opens first
     *  (after import success), then chains into the followup offer. */}
    <ChapterBibleDialog
      open={!!bible}
      projectId={bible?.project?.id}
      onOpenChange={(v) => {
        if (!v) {
          const captured = bible;
          setBible(null);
          // After bible closes, surface the Claude followup offer.
          if (captured) {
            setFollowup({ project: captured.project, synopsis: captured.synopsis });
          }
        }
      }}
      onSaved={async () => {
        // Reload project so character bible reflects new fields.
        if (bible?.project?.id) await loadProject(bible.project.id, { silent: true });
      }}
    />
    {/* iter-53m — Post-import Claude offer modal. Rendered as a sibling
     *  so it can stay open after the parent ImportSynopsisDialog closes. */}
    <SynopsisFollowupDialog
      open={!!followup}
      onOpenChange={(v) => { if (!v) setFollowup(null); }}
      project={followup?.project}
      synopsis={followup?.synopsis}
    />
    </>
  );
}
